import type { Schemas } from '@npc/api-client'
import { Link } from '@tanstack/react-router'
import { useState, type ReactNode } from 'react'

import { ActionTag } from './tag'
import { cn } from '../../components/cn'
import { StatusDot, Tag, type TagTone } from '../../components/tag'
import { bytes, hhmm, shortDate, daysBetween } from '../../app/format'
import { T } from '../../copy/text'

/**
 * 时间线上的一条。**两个源共用这一个组件**（ADR-0004）：
 * `kind: action` 是 AI 在机器上干的一件事，`kind: audit` 是人在控制面做的一件事。
 *
 * 契约把 `at` / `title` / `detail` / `tone` 提到了 `TimelineItem` 上，所以
 * **排版这一层不用先分支**——只有要展开细节、要判能不能撤时才看 `kind`。
 *
 * 流 A3 给的五条渲染规则在这个文件里，逐条标了出处。它们是实现时踩出来的，
 * 每一条都对应一个「照直觉写就会错」的地方。
 *
 * 哪几条对两个源都成立：
 *
 *   规则 1 / 2 / 3  只对 Action。审计记录没有 `undoable`，也没有那套状态机
 *   规则 4          **两个源都成立**。它是产品原则第 2 条，与数据来自哪儿无关
 *   规则 5          **两个源都成立**，而且正是它逼出了 `/timeline` 这个统一端点
 */
export interface EntryProps {
  item: Schemas['TimelineItem']
  onUndo: (actionId: string) => void
  undoing: boolean
  /** 撤失败时后端给的人话。只显示在出错的那一条上。 */
  undoError?: string
  /**
   * 点了「我同意，去做吧」。**只有 `awaiting_approval` 那一档会调到。**
   * 换二次验证令牌与真正那一下 POST 都在上层（`timeline/index.tsx`），
   * 这一层只负责把那个按钮画出来。
   */
  onApprove?: (actionId: string) => void
  /** 这个身份有没有资格批红级（`approve_red`）。**由后端的 capabilities 说了算。** */
  canApprove?: boolean
  approving?: boolean
  /** 批失败时后端给的人话。只显示在出错的那一条上。 */
  approveError?: string
}

export function Entry({
  item,
  onUndo,
  undoing,
  undoError,
  onApprove,
  canApprove = false,
  approving = false,
  approveError,
}: EntryProps) {
  const [open, setOpen] = useState(false)
  const action = item.action

  // 「看完整过程」点开的是命令、验证、输出、退出码、快照——**审计记录一样都没有**。
  // 给它一个点开是空的按钮，比不给更糟。
  //
  // **判断走契约给的 `detail_applicable`，不靠 `kind` 或者「有没有 action」猜**：
  // 猜等于把这个判断复制到前端一份，将来「哪些条目有完整过程可看」变了，
  // 两边就分叉了（契约里 detail_applicable 那段说的就是这件事）。
  // 还要 action 在手才展得开，所以两个条件都要。
  const expandable = item.detail_applicable && Boolean(action)

  return (
    // `data-action-id` 让用例锚在**具体哪一条**上，而不是数第几个按钮。
    // 位置索引（`detail[3]`）在夹具加一条时会静静地指向另一条记录，
    // 而那种失败读起来像是被测代码坏了。
    <div className="flex items-start gap-npc-grid-lg" data-testid="timeline-entry" data-action-id={action?.id}>
      <Stamp iso={item.at} />

      <div className="flex-none pt-[22px]">
        <StatusDot tone={dotTone(item)} className="mt-[5px]" />
      </div>

      <div className="mb-[10px] min-w-0 flex-1 rounded-npc-card-md border border-npc-border bg-npc-panel px-[17px] py-[15px] shadow-npc-card">
        <div className="mb-npc-gap-xs flex items-center gap-npc-gap-sm">
          {/* 规则 4：第一屏只有这一句。它是后端渲染好的人话——
              Action 那边由 cause.reason_code 加变量出，审计那边由 verb 加对象出 */}
          <span className="text-npc-card-title font-semibold">{item.title}</span>
          {action?.tag ? <ActionTag tag={action.tag} /> : null}
        </div>

        {item.detail ? (
          <p className="m-0 text-npc-body-sm leading-[1.65] text-npc-ink-medium">{item.detail}</p>
        ) : null}

        {action ? <Outcome action={action} /> : null}

        {open && action ? <FullProcess action={action} /> : null}

        <div className="mt-npc-grid-sm flex flex-wrap items-center gap-npc-gap-lg text-npc-caption-sm">
          <Where item={item} />

          {expandable ? (
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpen((value) => !value)}
              className="cursor-pointer border-none bg-transparent p-0 font-[inherit] text-npc-caption-sm text-npc-green"
            >
              <T id="timeline.action.detail" />
            </button>
          ) : null}

          <Undo item={item} onUndo={onUndo} undoing={undoing} />

          <Approve
            item={item}
            onApprove={onApprove}
            canApprove={canApprove}
            approving={approving}
          />
        </div>

        {undoError ? (
          <p className="mt-npc-gap-sm mb-0 text-npc-caption-sm text-npc-red-ink">{undoError}</p>
        ) : null}

        {approveError ? (
          <p role="alert" className="mt-npc-gap-sm mb-0 text-npc-caption-sm text-npc-red-ink">
            {approveError}
          </p>
        ) : null}
      </div>
    </div>
  )
}

// ------------------------------------------------------ 规则 2：两种「没算数」

/**
 * `rolled_back` 与 `undone` 必须看得出是两回事。
 *
 *   `rolled_back` **它自己没成**，系统按执行前的快照退回去了
 *   `undone`      **它成了**，你事后点了「撤销这次改动」
 *
 * 混成一种，用户看不出到底发生了什么。这是可解释性底线，不是配色偏好。
 *
 * 现在两边各有一条**自己的**模板（`timeline.state.*`），差别全在主语上：
 * 「**我**退回去了」是系统干的，「**你**把这次撤了」是用户干的。
 *
 * 上一版没有这两条模板，靠的是「取 `result.failure_text`」+「引用撤销那一条的
 * `title`」推出来。那套推导有两个空档（`failure_text` 契约允许留空、
 * 撤销那条可能不在这一页），现在退成锦上添花：
 *
 *   `rolled_back` 状态句 + `failure_text`（**它补的是「为什么没成」，不重复**）
 *   `undone`      只要状态句。撤销那条的 title 是「撤回来了，恢复成动手之前的样子」，
 *                 与「你把这次撤了」说的是同一件事，两句并排是噪音
 */
function Outcome({ action }: { action: Schemas['Action'] }) {
  if (action.status === 'undone') {
    return (
      <p className="m-0 text-npc-body-sm leading-[1.65] text-npc-ink-medium">
        <T id="timeline.state.undone" />
      </p>
    )
  }

  const failure = action.result?.failure_text

  if (action.status === 'rolled_back') {
    return (
      /* 两句分两行。挤在一行上「没成，我退回去了」后面紧跟失败原因，
         中间又没有标点（状态句本身不带句号），读起来像一句没断开的话 */
      <div className="text-npc-body-sm leading-[1.65]">
        <p className="m-0 text-npc-amber-ink">
          <T id="timeline.state.rolled_back" />
        </p>
        {failure ? <p className="m-0 text-npc-ink-medium">{failure}</p> : null}
      </div>
    )
  }

  // 单纯失败（没退回去）没有状态句，只有失败原因。填不出来就留空——
  // 契约明写「不要兜底填一句笼统的」
  if (failure && action.status === 'failed') {
    return <p className="m-0 text-npc-body-sm leading-[1.65] text-npc-red-ink">{failure}</p>
  }

  return null
}

// ------------------------------------------------ 等你点头那一下

/**
 * 「我同意，去做吧」。**只在 `awaiting_approval` 那一档出现。**
 *
 * ------------------------------------------------------------------
 * # 它与旁边的「撤销这次改动」语气刻意相反
 *
 * 撤销那条路要比动手更好走（产品原则第 3 条），所以它旁边一句吓人的话都不加，
 * 也不要二次验证。而这一下是**让一件收不回来的事发生**，所以：
 * 先弹一句说清代价的确认，再走一次「确认是你本人」。
 *
 * # 没资格的时候不给按钮，而且要说清为什么
 *
 * 「干活的」这一档没有 `approve_red`，**按多少次确认都不会通过**。
 * 给一个按得下去的按钮，就是一个让人一直点、一直失败、还看不出为什么的
 * 死循环（`errs.Forbidden` 的注释说的是同一件事）。所以这里给的是一句话，
 * 而且那句话给出他真正能做的下一步（转给老板）。
 *
 * # 判据是后端给的 capabilities，不是角色名
 *
 * **不写 `role === 'owner'`。** 权限规则有两份的那天，
 * UI 上能点的和后端认的就对不上了，而对不上的方向多半是
 * 「看着能点，点了报错」（`GET /account` 那段契约说的就是这件事）。
 * ------------------------------------------------------------------
 */
function Approve({
  item,
  onApprove,
  canApprove,
  approving,
}: {
  item: Schemas['TimelineItem']
  onApprove?: (actionId: string) => void
  canApprove: boolean
  approving: boolean
}) {
  const action = item.action
  if (!action || action.status !== 'awaiting_approval') return null

  if (!canApprove) {
    return (
      <span className="text-npc-caption-sm text-npc-ink-softer" data-testid="timeline-approve-forbidden">
        <T id="timeline.approve.forbidden" />
      </span>
    )
  }

  return (
    <button
      type="button"
      disabled={approving || !onApprove}
      data-testid="timeline-approve"
      onClick={() => onApprove?.(action.id)}
      className="cursor-pointer border-none bg-transparent p-0 font-[inherit] text-npc-caption-sm text-npc-amber-ink disabled:cursor-not-allowed"
    >
      <T id="timeline.action.approve" />
    </button>
  )
}

// ------------------------------------------------ 规则 1 与 3：撤销这次改动

/**
 * 「撤销这次改动」。**两个字段，两个问题，顺序不能反：**
 *
 *   `item.undo_applicable`   「撤销」这个概念对这一条**适不适用**
 *   `action.undoable`        适用的前提下，这一条**现在能不能撤**
 *
 * 不适用时**整个按钮不出现**。审计记录（「谁改了服务商的 key」）就是这一档——
 * 在它旁边摆一个灰着的撤销按钮，是在暗示它本来可以撤，
 * 而真相是「撤销一次建项目 = 归档它，那是另一个动作」（ADR-0004）。
 *
 * 适用但撤不了时**按钮显示成灰的，而且要说清为什么**（规则 3）——
 * 灰按钮加一片空白比不给按钮更糟：用户会以为是自己没找对地方。
 * 这两种恰好相反，所以契约把它们分成两个字段，**不让前端靠 `kind` 猜**。
 *
 * 能撤时，`undo.interrupts_service` 为真还要把代价说出来：
 * 回滚容器会重启，撤销一次约等于服务中断 1 秒（流 E 实测）。
 * **为假或缺省时什么都不加——默认不吓人。**
 */
function Undo({
  item,
  onUndo,
  undoing,
}: {
  item: Schemas['TimelineItem']
  onUndo: (actionId: string) => void
  undoing: boolean
}) {
  if (!item.undo_applicable) return null

  const action = item.action
  // **能不能撤由契约上的 `item.undoable` 说了算**，不去 action 里掏。
  // 契约把它和 `undo_applicable` 一起提到条目上，正是为了前端不必知道
  // 「这一条底下挂的是 Action 还是审计记录」。
  const undoable = item.undoable

  const label = (
    <span className={cn('text-npc-caption-sm', undoable ? 'text-npc-ink-soft' : 'text-npc-ink-faint')}>
      <T id="timeline.action.undo" />
    </span>
  )

  if (!undoable || !action) {
    return (
      <span className="flex items-center gap-npc-gap-xs">
        <span aria-disabled="true">{label}</span>
        {item.not_undoable_text ? (
          <span className="text-npc-caption-sm text-npc-ink-softer">{item.not_undoable_text}</span>
        ) : null}
      </span>
    )
  }

  return (
    <span className="flex items-center gap-npc-gap-xs">
      <button
        type="button"
        disabled={undoing}
        onClick={() => onUndo(action.id)}
        className="cursor-pointer border-none bg-transparent p-0 font-[inherit] disabled:cursor-not-allowed"
      >
        {label}
      </button>
      {action.undo.interrupts_service ? (
        // 说明，不是警告：不加感叹号、不上琥珀色。产品原则第 3 条要求
        // 撤销这条路比动手更好走，在它前面竖一块吓人的牌子是把原则反过来用
        <span className="text-npc-caption-sm text-npc-ink-softer">
          <T id="timeline.undo.cost.restart" />
        </span>
      ) : null}
    </span>
  )
}

// -------------------------------------------------- 规则 4：看完整过程之后

/**
 * 「看完整过程」展开的那一层。
 *
 * 上一版这里是一个**没有小标题的深色块**，因为当时 `docs/copy/` 里没有能当
 * 小标题的模板，而我不肯自己编「执行的命令」这类词。代价是**退出码与快照
 * 根本没显示**——一个光秃秃的 `0` 或一串文件路径，没有词解释它是什么，
 * 比不显示更糟。
 *
 * 六条小标题到位了，所以这一层现在是五块：
 *
 *   它执行了这些          `commands`
 *   做完之后我验了一下     `checks` —— **与上面分开显示**，混在一起看不出哪些在改哪些在看
 *   机器回了这些          `stdout_tail` / `stderr_tail`
 *   跑完了，没报错 / 跑挂了 `exit_code`
 *   动手之前我拍了一份     `snapshot`
 *
 * **最后一块是这一层里最要紧的。** 它是产品原则第 3 条「可回退比可控制重要」
 * 在界面上的落点：用户看得见快照真的拍了、拍了什么、多大，
 * 才会相信「一键撤销」不是一句话术。
 *
 * `ActionCommand` 不是一条 shell 字符串：`program` 是可执行文件、`args` 是数组，
 * 探针直接 exec 不经过 `/bin/sh`。这里拼起来只是为了给人看，
 * **不要把这个字符串当成可以复制去执行的命令**。
 */
function FullProcess({ action }: { action: Schemas['Action'] }) {
  const tails = [action.result?.stdout_tail, action.result?.stderr_tail].filter(
    (tail): tail is string => Boolean(tail),
  )
  const exitCode = action.result?.exit_code

  return (
    <div className="mt-npc-gap flex flex-col gap-npc-grid-sm">
      {action.commands.length > 0 ? (
        <Block label={<T id="timeline.detail.commands" />}>
          {action.commands.map((command, index) => (
            <div key={index}>{line(command)}</div>
          ))}
        </Block>
      ) : null}

      {action.checks && action.checks.length > 0 ? (
        <Block label={<T id="timeline.detail.checks" />}>
          {action.checks.map((command, index) => (
            <div key={index}>{line(command)}</div>
          ))}
        </Block>
      ) : null}

      {tails.length > 0 ? (
        <Block label={<T id="timeline.detail.output" />}>
          {tails.map((tail, index) => (
            <div key={index} className="whitespace-pre-wrap">
              {tail}
            </div>
          ))}
        </Block>
      ) : null}

      {exitCode !== undefined ? (
        <Tag tone={exitCode === 0 ? 'ok' : 'danger'} className="self-start">
          <T id={exitCode === 0 ? 'timeline.detail.exit_ok' : 'timeline.detail.exit_failed'} />
        </Tag>
      ) : null}

      {action.snapshot ? <Snapshot snapshot={action.snapshot} /> : null}
    </div>
  )
}

/** 一段机器说的话。深色底 + 等宽字，与设计稿那个块同一个样子。 */
function Block({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div>
      <div className="mb-npc-gap-xs text-npc-caption-sm text-npc-ink-soft">{label}</div>
      <div className="rounded-npc-code bg-npc-dark px-[13px] py-[11px] font-npc-mono text-npc-caption-sm leading-[1.8] text-npc-code-ink">
        {children}
      </div>
    </div>
  )
}

/**
 * 「动手之前我拍了一份」。
 *
 * **不放在深色块里**，那是机器说的话；快照是给用户看的凭据。
 * 每一项一行：拍的是什么、多大。**没有写「共 N 项」**——
 * 「项」是个字，得走模板，而列表本身已经把条数摆在那儿了。
 */
function Snapshot({ snapshot }: { snapshot: Schemas['ActionSnapshot'] }) {
  return (
    <div>
      <div className="mb-npc-gap-xs text-npc-caption-sm text-npc-ink-soft">
        <T id="timeline.detail.snapshot" />
      </div>
      <div className="rounded-npc-code border border-npc-border-soft bg-npc-surface-warm px-[13px] py-[11px]">
        {snapshot.items.map((item, index) => (
          <div key={index} className="flex items-baseline justify-between gap-npc-gap-lg">
            <span className="font-npc-mono text-npc-caption-sm text-npc-ink-medium">
              {item.target}
            </span>
            {item.bytes ? (
              <span className="shrink-0 font-npc-mono text-npc-meta text-npc-ink-softer">
                {bytes(item.bytes)}
              </span>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  )
}

function line(command: Schemas['ActionCommand']): string {
  return [command.program, ...(command.args ?? [])].join(' ')
}

// ------------------------------------------------------------------ 零碎

/**
 * 卡片底下那一行「在哪儿」。
 *
 * **有机器就只显示机器名，没有才退到项目名。不把两个拼起来。**
 *
 * 拼起来试过，结果是「宠物寄养 · 宠物寄养 · 后端」。原因不是 mock 数据没写好：
 * arron 给机器起名就是「项目 · 角色」这个格式（设计稿里 20 台机器全是这么叫的），
 * 所以项目名本来就已经在机器名里了，再拼一次必然重。
 *
 * 这一格真正该显示的是设计稿那种随情况变的句子——「家政派单 · 主机 4 台」
 * 「英语陪练 · fix/audio-leak」「全部机器」。那是一句**后端渲染好的话**，
 * 拿两个结构化引用拼不出来（`host` / `project` 为空只能表示「没有」，
 * 表示不了「全部」）。契约里缺的那个字段是 `Action.scope_text`，
 * 已经提给流 0（`m3-需要的契约.md` §8.3）。补上之后这个函数整个换成显示它。
 */
function Where({ item }: { item: Schemas['TimelineItem'] }) {
  // `host` / `project` 在**条目**上，不在 `action` 里（服务端
  // `a3api/timeline.go` 的 timelineItemDTO 就是这么装的）。审计那一支
  // 压根没有这两个字段，往下掏会掏空。
  const host = item.host
  const project = item.project

  if (host) {
    return (
      <Link
        to="/hosts/$hostId"
        params={{ hostId: host.id }}
        className="text-npc-caption-sm text-npc-ink-softer"
      >
        {host.name}
      </Link>
    )
  }

  if (!project) return null
  return <span className="text-npc-caption-sm text-npc-ink-softer">{project.name}</span>
}

/**
 * 左边那一列的时间戳。设计稿那一列是混排的：
 * `04:55` / `昨天 19:20` / `7/29 03:41`——近两天说人话，再往前给日期。
 *
 * 「昨天」走 `timeline.stamp.yesterday`，**不借机器详情那边的
 * `host.recent.yesterday`**：两条内容一样但各归各屏，哪天一边改说法，
 * 另一边不会跟着莫名其妙地变。
 */
function Stamp({ iso }: { iso: string }) {
  const days = daysBetween(iso)

  return (
    <time
      dateTime={iso}
      className="w-[44px] flex-none pt-[20px] text-right font-npc-mono text-npc-meta text-npc-ink-softer"
    >
      {days === 1 ? (
        // 「昨天」是中文，走 sans；时间还是等宽，两种字体不混在一个 span 里
        <span className="block font-npc-sans">
          <T id="timeline.stamp.yesterday" />
        </span>
      ) : days > 1 ? (
        <span className="block">{shortDate(iso)}</span>
      ) : null}
      {hhmm(iso)}
    </time>
  )
}

/**
 * 左边那个点用什么颜色。
 *
 * 后端给了 `tone` 就用它——「什么情况算警告」是后端的判断，前端再判一次
 * 一定会走偏。没给的时候才退回到 `status`，而且只认那几个**明确**的终态，
 * 剩下的一律 neutral：猜错的颜色比没有颜色更糟。
 */
function dotTone(item: Schemas['TimelineItem']): TagTone {
  if (item.tone) return item.tone

  // 后端没给 tone 时，只有 Action 那种还能从终态退一步推；审计记录没有状态机，
  // 一律 neutral。**只认那几个明确的**，剩下的不猜——
  // 猜错的颜色比没有颜色更糟
  switch (item.action?.status) {
    case 'succeeded':
      return 'ok'
    case 'failed':
      return 'danger'
    case 'rolled_back':
    case 'awaiting_approval':
      return 'warn'
    default:
      return 'neutral'
  }
}
